import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { StoreProvider } from './context/StoreContext';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import StoresPage from './pages/StoresPage';
import SalesEntryPage from './pages/SalesEntryPage';
import FuelManagementPage from './pages/FuelManagementPage';
import PurchasesPage from './pages/PurchasesPage';
import SuppliersPage from './pages/SuppliersPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import AuditLogPage from './pages/AuditLogPage';
import BackupPage from './pages/BackupPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ScratchCardsPage from './pages/ScratchCardsPage';
import LotteryPage from './pages/LotteryPage';
import MoneyTransferPage from './pages/MoneyTransferPage';
import CreditorsPage from './pages/CreditorsPage';
import DriveOffDebtPage from './pages/DriveOffDebtPage';
import CommissionIncomePage from './pages/CommissionIncomePage';
import FuelCardBalancesPage from './pages/FuelCardBalancesPage';
import BankStatementsPage from './pages/BankStatementsPage';
import ReconciliationPage from './pages/ReconciliationPage';
import CreditCardsPage from './pages/CreditCardsPage';
import StaffHoursPage from './pages/StaffHoursPage';
import LoansPage from './pages/LoansPage';
import VouchersPage from './pages/VouchersPage';
import ExpensesPage from './pages/ExpensesPage';
import CreditorDashboardPage from './pages/CreditorDashboardPage';
import CreditorRequestFormPage from './pages/CreditorRequestFormPage';
import CreditorRequestHistoryPage from './pages/CreditorRequestHistoryPage';
import FuelPricesPage from './pages/FuelPricesPage';
import TillMappingPage from './pages/TillMapping/TillMappingPage';
import ApprovalRequestsPage from './pages/ApprovalRequestsPage';

// Layout
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if password change is required
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Restrict creditor users to only creditor routes
  if (user.user_type === 'creditor') {
    const allowedPaths = ['/creditor-dashboard', '/creditor-requests/new', '/creditor-requests/history', '/creditor-requests/edit', '/change-password'];
    const currentPath = window.location.pathname;
    const isAllowed = allowedPaths.some(path => currentPath.startsWith(path));
    
    if (!isAllowed) {
      return <Navigate to="/creditor-dashboard" replace />;
    }
  }

  return <Layout>{children}</Layout>;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (user && !user.must_change_password) {
    // Redirect creditor users to their dashboard
    if (user.user_type === 'creditor') {
      return <Navigate to="/creditor-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sales-entry" 
        element={
          <ProtectedRoute>
            <SalesEntryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/fuel" 
        element={
          <ProtectedRoute>
            <FuelManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases" 
        element={
          <ProtectedRoute>
            <PurchasesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scratch-cards" 
        element={
          <ProtectedRoute>
            <ScratchCardsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/lottery" 
        element={
          <ProtectedRoute>
            <LotteryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/money-movement" 
        element={
          <ProtectedRoute>
            <MoneyTransferPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/creditors" 
        element={
          <ProtectedRoute>
            <CreditorsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/drive-off-debt" 
        element={
          <ProtectedRoute>
            <DriveOffDebtPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/suppliers" 
        element={
          <ProtectedRoute>
            <SuppliersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/staff-hours" 
        element={
          <ProtectedRoute>
            <StaffHoursPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/stores" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <StoresPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AuditLogPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/backup" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <BackupPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/commission-income" 
        element={
          <ProtectedRoute>
            <CommissionIncomePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/fuel-card-balances" 
        element={
          <ProtectedRoute>
            <FuelCardBalancesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bank-statements" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <BankStatementsPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/credit-cards" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <CreditCardsPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reconciliation" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <ReconciliationPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/loans" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <LoansPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vouchers" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <VouchersPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/expenses" 
        element={
          <ProtectedRoute>
            <ExpensesPage />
          </ProtectedRoute>
        } 
      />

      {/* Creditor User Routes */}
      <Route 
        path="/creditor-dashboard" 
        element={
          <ProtectedRoute>
            <CreditorDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/creditor-requests/new" 
        element={
          <ProtectedRoute>
            <CreditorRequestFormPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/creditor-requests/history" 
        element={
          <ProtectedRoute>
            <CreditorRequestHistoryPage />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/till-mapping" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <TillMappingPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />

      {/* Fuel Prices - Admin Only */}
      <Route 
        path="/fuel-prices" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <FuelPricesPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />

      {/* Approval Requests - Admin Only */}
      <Route 
        path="/approval-requests" 
        element={
          <ProtectedRoute>
            <AdminRoute>
              <ApprovalRequestsPage />
            </AdminRoute>
          </ProtectedRoute>
        } 
      />

      {/* Default Route */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <AuthProvider>
        <StoreProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster 
              position="top-right" 
              theme="dark"
              toastOptions={{
                style: {
                  background: '#18181b',
                  border: '1px solid #27272a',
                  color: '#fafafa'
                }
              }}
            />
          </BrowserRouter>
        </StoreProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
